/*
 * <purchase-menu> — product page purchase options (snippets/purchase-menu.liquid).
 *
 * Each radio carries the cart payload of its option (data-variant-id, data-quantity,
 * data-selling-plan). The product form itself is rendered by the buy_buttons block and
 * submitted by ProductForm (theme.js) with `new FormData(form)`, so this element writes the
 * selected option into the form's `id`, `quantity` and `selling_plan` fields ONLY at submit time,
 * in the capture phase on document, i.e. just before ProductForm reads the form. Until then the
 * form keeps the product page's own variant, so other scripts reading it are not misled by a
 * coffret variant id, and a value written meanwhile by another script (Seal widget…) is overridden.
 * Seal 3- and 6-month options: each adds TWO lines (the paid boxes + the gift line with property
 * _seal_gift: masks for 3 months, one more box for 6 months), listed in the radio's data-items.
 * FormData can only carry one line, so for those options this element stops ProductForm and posts
 * the items itself, then does what ProductForm does after an add (bundled sections, cartUpdate
 * event, ajaxProduct:added, cart drawer).
 */
(function () {
  if (customElements.get('purchase-menu')) return;

  class PurchaseMenu extends HTMLElement {
    connectedCallback() {
      this.formId = this.getAttribute('data-form-id');
      this.error = this.querySelector('.mps-pm__error');

      this.onChange = (event) => {
        if (event.target.matches('input[type="radio"]')) this.update();
      };
      this.onSubmit = (event) => this.handleSubmit(event);

      this.addEventListener('change', this.onChange);
      document.addEventListener('submit', this.onSubmit, true);
      this.update();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      document.removeEventListener('submit', this.onSubmit, true);
    }

    get form() {
      return document.getElementById(this.formId);
    }

    get selected() {
      return this.querySelector('input[type="radio"]:checked:not(:disabled)');
    }

    update() {
      this.querySelectorAll('.mps-pm__option').forEach((option) => {
        option.classList.toggle('is-selected', !!option.querySelector('input[type="radio"]:checked'));
      });
      if (this.selected && this.error) this.error.hidden = true;
      // The line under the button (buy-buttons.liquid) only speaks of the subscription
      const footer = document.querySelector(`[data-purchase-menu-footer="${this.formId}"]`);
      if (footer) footer.hidden = !(this.selected && this.selected.dataset.sellingPlan);
    }

    handleSubmit(event) {
      const form = this.form;
      if (!form || event.target !== form) return;

      if (!this.selected) {
        // Nothing chosen: block the add (ProductForm listens on the form, after this capture listener)
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.error) this.error.hidden = false;
        const first = this.querySelector('input[type="radio"]:not(:disabled)');
        if (first) first.focus();
        return;
      }

      const items = this.itemsOf(this.selected);
      if (items || this.selected.dataset.items) {
        event.preventDefault();
        event.stopImmediatePropagation();
        // Unreadable items: never let ProductForm add the paid boxes without their gift
        if (items) this.addItems(form, items, event.submitter);
        else this.showFormError(form);
        return;
      }
      this.writeTo(form);
    }

    itemsOf(input) {
      if (!input.dataset.items) return null;
      try {
        const items = JSON.parse(input.dataset.items);
        return Array.isArray(items) && items.length > 0 ? items : null;
      } catch (error) {
        return null;
      }
    }

    addItems(form, items, submitter) {
      if (this.adding) return;
      // Every submit button of the form, including the sticky one outside it (form="…"), like ProductForm
      const buttons = Array.from(form.querySelectorAll('[type="submit"]'));
      document.querySelectorAll(`[type="submit"][form="${form.id}"]`).forEach((element) => {
        if (!buttons.includes(element)) buttons.push(element);
      });
      const button = submitter || buttons[0];
      // aria-busy = an add by ProductForm in flight, set on the form's own button only, not on the
      // sticky one (aria-disabled can stay after a sold-out error, so it is not checked)
      if (buttons.some((element) => element.hasAttribute('aria-busy'))) return;
      this.adding = true;
      const errorBox = form.querySelector('.product-form__error-message');
      if (errorBox) errorBox.hidden = true;
      buttons.forEach((element) => {
        element.setAttribute('aria-disabled', 'true');
        element.setAttribute('aria-busy', 'true');
      });

      // Same request as ProductForm (theme.js), with a JSON items list instead of FormData
      const pageCart = document.body.classList.contains('template-cart') || theme.settings.cartType === 'page';
      const sections = [];
      if (!pageCart) {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sections } })
        );
      }
      const config = theme.utils.fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = JSON.stringify({ items: items, sections: sections.join(','), sections_url: window.location.pathname });

      let added = false;
      let leaving = false;
      fetch(theme.routes.cart_add_url, config)
        .then((response) => response.json())
        .then(async (state) => {
          if (state.status) {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartError, {
              source: 'purchase-menu',
              productVariantId: items[0].id,
              errors: state.errors || state.description,
              message: state.message,
            });
            if (errorBox && form.getAttribute('data-hide-errors') !== 'true') {
              errorBox.innerText = state.description || state.message || '';
              errorBox.hidden = false;
            }
            return;
          }
          added = true;
          if (pageCart) {
            leaving = true;
            window.location.href = theme.routes.cart_url;
            return;
          }
          const cart = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
          cart.sections = state.sections;
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'purchase-menu',
            productVariantId: items[0].id,
            cart: cart,
          });
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: state } }));
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.show === 'function') drawer.show(button);
        })
        .catch((error) => {
          console.error(error);
          // Added, but the cart refresh failed: show the cart rather than invite a second add
          if (added) {
            leaving = true;
            window.location.href = theme.routes.cart_url;
          } else {
            this.showFormError(form);
          }
        })
        .finally(() => {
          // Going to the cart page: buttons stay disabled so no second add starts meanwhile
          if (leaving) return;
          this.adding = false;
          buttons.forEach((element) => {
            element.removeAttribute('aria-busy');
            element.removeAttribute('aria-disabled');
          });
        });
    }

    // The product form's own error box (buy-buttons.liquid), generic cart error by default
    showFormError(form, message) {
      const errorBox = form.querySelector('.product-form__error-message');
      if (!errorBox || form.getAttribute('data-hide-errors') === 'true') return;
      errorBox.innerText = message || theme.cartStrings.error;
      errorBox.hidden = false;
    }

    writeTo(form) {
      const input = this.selected;
      if (!input) return;
      this.setField(form, 'id', input.dataset.variantId);
      this.setField(form, 'quantity', input.dataset.quantity || '1');
      this.setField(form, 'selling_plan', input.dataset.sellingPlan || '');
    }

    // Sets every field of that name owned by the form (including inputs linked with form="…"),
    // creating a hidden one when the form has none.
    setField(form, name, value) {
      const fields = Array.from(form.elements).filter((element) => element.name === name);
      if (fields.length === 0) {
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = name;
        form.appendChild(hidden);
        fields.push(hidden);
      }
      fields.forEach((field) => {
        field.disabled = false;
        field.value = value;
      });
    }
  }

  customElements.define('purchase-menu', PurchaseMenu);
})();
